import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CanvasElement, ElementStyle, CanvasSettings } from './CanvasEditor';
import { ColorPicker, GradientPicker } from './ColorPicker';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';

interface ElementPropertiesProps {
  element: CanvasElement;
  canvas: CanvasSettings; // Für visuelle Symmetrie-Berechnung bei Kreisen
  onChange: (element: CanvasElement) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onOpenMediaPicker?: () => void;
}

// Schriftarten - gruppiert nach Kategorie
const FONTS = [
  // === HANDSCHRIFT & SCRIPT (Feminin/Elegant) ===
  { value: 'Dancing Script', label: 'Dancing Script', category: 'script' },
  { value: 'Pacifico', label: 'Pacifico', category: 'script' },
  { value: 'Great Vibes', label: 'Great Vibes', category: 'script' },
  { value: 'Satisfy', label: 'Satisfy', category: 'script' },
  { value: 'Allura', label: 'Allura', category: 'script' },
  { value: 'Sacramento', label: 'Sacramento', category: 'script' },
  { value: 'Tangerine', label: 'Tangerine', category: 'script' },
  { value: 'Amatic SC', label: 'Amatic SC', category: 'script' },
  { value: 'Kalam', label: 'Kalam', category: 'script' },
  { value: 'Caveat', label: 'Caveat', category: 'script' },
  { value: 'Indie Flower', label: 'Indie Flower', category: 'script' },
  { value: 'Shadows Into Light', label: 'Shadows Into Light', category: 'script' },
  { value: 'Lobster', label: 'Lobster', category: 'script' },
  { value: 'Courgette', label: 'Courgette', category: 'script' },
  { value: 'Petit Formal Script', label: 'Petit Formal Script', category: 'script' },
  { value: 'Pinyon Script', label: 'Pinyon Script', category: 'script' },
  { value: 'Alex Brush', label: 'Alex Brush', category: 'script' },
  { value: 'Marck Script', label: 'Marck Script', category: 'script' },
  { value: 'Cookie', label: 'Cookie', category: 'script' },
  { value: 'Yellowtail', label: 'Yellowtail', category: 'script' },
  { value: 'Homemade Apple', label: 'Homemade Apple', category: 'script' },
  { value: 'Nothing You Could Do', label: 'Nothing You Could Do', category: 'script' },
  { value: 'Reenie Beanie', label: 'Reenie Beanie', category: 'script' },
  { value: 'La Belle Aurore', label: 'La Belle Aurore', category: 'script' },
  { value: 'Cedarville Cursive', label: 'Cedarville Cursive', category: 'script' },

  // === MODERNE SANS-SERIF ===
  { value: 'Avenir Next', label: 'Avenir Next', category: 'sans' },
  { value: 'Inter', label: 'Inter', category: 'sans' },
  { value: 'Montserrat', label: 'Montserrat', category: 'sans' },
  { value: 'Poppins', label: 'Poppins', category: 'sans' },
  { value: 'Roboto', label: 'Roboto', category: 'sans' },
  { value: 'Open Sans', label: 'Open Sans', category: 'sans' },
  { value: 'Lato', label: 'Lato', category: 'sans' },
  { value: 'Nunito', label: 'Nunito', category: 'sans' },
  { value: 'Quicksand', label: 'Quicksand', category: 'sans' },
  { value: 'Raleway', label: 'Raleway', category: 'sans' },
  { value: 'Work Sans', label: 'Work Sans', category: 'sans' },
  { value: 'Josefin Sans', label: 'Josefin Sans', category: 'sans' },
  { value: 'Comfortaa', label: 'Comfortaa', category: 'sans' },
  { value: 'Karla', label: 'Karla', category: 'sans' },
  { value: 'Arial', label: 'Arial', category: 'sans' },
  { value: 'Helvetica', label: 'Helvetica', category: 'sans' },
  { value: 'Verdana', label: 'Verdana', category: 'sans' },

  // === SERIF (Elegant/Klassisch) ===
  { value: 'Playfair Display', label: 'Playfair Display', category: 'serif' },
  { value: 'Cormorant Garamond', label: 'Cormorant Garamond', category: 'serif' },
  { value: 'Libre Baskerville', label: 'Libre Baskerville', category: 'serif' },
  { value: 'EB Garamond', label: 'EB Garamond', category: 'serif' },
  { value: 'Lora', label: 'Lora', category: 'serif' },
  { value: 'Merriweather', label: 'Merriweather', category: 'serif' },
  { value: 'Crimson Text', label: 'Crimson Text', category: 'serif' },
  { value: 'Source Serif Pro', label: 'Source Serif Pro', category: 'serif' },
  { value: 'Georgia', label: 'Georgia', category: 'serif' },
  { value: 'Times New Roman', label: 'Times New Roman', category: 'serif' },

  // === DISPLAY/DEKORATIV ===
  { value: 'Abril Fatface', label: 'Abril Fatface', category: 'display' },
  { value: 'Bebas Neue', label: 'Bebas Neue', category: 'display' },
  { value: 'Oswald', label: 'Oswald', category: 'display' },
  { value: 'Anton', label: 'Anton', category: 'display' },
  { value: 'Righteous', label: 'Righteous', category: 'display' },
  { value: 'Fredoka One', label: 'Fredoka One', category: 'display' },
  { value: 'Paytone One', label: 'Paytone One', category: 'display' },
  { value: 'Passion One', label: 'Passion One', category: 'display' },

  // === MONOSPACE ===
  { value: 'monospace', label: 'Monospace', category: 'mono' },
  { value: 'Fira Code', label: 'Fira Code', category: 'mono' },
  { value: 'Source Code Pro', label: 'Source Code Pro', category: 'mono' },
];

// Form-Typen - Keys werden per i18n aufgeloest
const SHAPE_TYPE_KEYS = [
  { value: 'rectangle', key: 'rectangle' },
  { value: 'circle', key: 'circle' },
  { value: 'ellipse', key: 'ellipse' },
  { value: 'triangle', key: 'triangle' },
  { value: 'line', key: 'line' },
  { value: 'star', key: 'star' },
];

// Text-Eingabe mit Emoji-Picker
function TextInputWithEmoji({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const { t } = useTranslation(['creator']);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Schliesse Picker bei Klick ausserhalb
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    if (showEmojiPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const textarea = textareaRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = value.substring(0, start) + emojiData.emoji + value.substring(end);
      onChange(newValue);
      // Cursor nach dem Emoji positionieren
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emojiData.emoji.length;
        textarea.focus();
      }, 0);
    } else {
      onChange(value + emojiData.emoji);
    }
  };

  return (
    <div className="relative">
      <div className="flex items-center justify-between mb-1">
        <label className="block text-sm font-medium text-gray-700">{t('creator:text')}</label>
        <button
          type="button"
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className={`p-1.5 rounded-lg transition-colors ${
            showEmojiPicker
              ? 'bg-yellow-100 text-yellow-600'
              : 'hover:bg-gray-100 text-gray-500'
          }`}
          title={t('creator:properties.insertEmoji')}
        >
          <span className="text-lg">😊</span>
        </button>
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg resize-none"
        rows={3}
        placeholder={t('creator:properties.textPlaceholder')}
      />
      {showEmojiPicker && (
        <div
          ref={pickerRef}
          className="absolute z-50 right-0 mt-1"
        >
          <EmojiPicker
            onEmojiClick={handleEmojiClick}
            theme={Theme.LIGHT}
            width={300}
            height={350}
            searchPlaceholder={t('creator:properties.searchEmoji')}
            previewConfig={{ showPreview: false }}
          />
        </div>
      )}
    </div>
  );
}

export function ElementProperties({
  element,
  canvas,
  onChange,
  onDelete,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onOpenMediaPicker,
}: ElementPropertiesProps) {
  const { t } = useTranslation(['creator', 'common']);

  const updateStyle = (updates: Partial<ElementStyle>) => {
    onChange({
      ...element,
      style: { ...element.style, ...updates },
    });
  };

  const updateContent = (content: string) => {
    onChange({ ...element, content });
  };

  return (
    <div className="p-4 space-y-4 overflow-y-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-gray-900">
          {element.type === 'text' && t('creator:text')}
          {element.type === 'image' && t('creator:image')}
          {element.type === 'shape' && t('creator:shape')}
          {element.type === 'audio' && t('creator:audio')}
        </h3>
        <div className="flex items-center gap-1">
          <button
            onClick={onMoveUp}
            className="p-1 hover:bg-gray-100 rounded"
            title={t('creator:properties.moveForward')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
            </svg>
          </button>
          <button
            onClick={onMoveDown}
            className="p-1 hover:bg-gray-100 rounded"
            title={t('creator:properties.moveBackward')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <button
            onClick={onDuplicate}
            className="p-1 hover:bg-gray-100 rounded"
            title={t('common:duplicate')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          </button>
          <button
            onClick={onDelete}
            className="p-1 hover:bg-red-100 text-red-600 rounded"
            title={t('common:delete')}
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Position & Groesse */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">{t('creator:properties.positionAndSize')}</label>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-xs text-gray-500">X (%)</label>
            <input
              type="number"
              value={Math.round(element.position.x * 10) / 10}
              onChange={(e) => onChange({ ...element, position: { ...element.position, x: parseFloat(e.target.value) || 0 } })}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              step="0.5"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">Y (%)</label>
            <input
              type="number"
              value={Math.round(element.position.y * 10) / 10}
              onChange={(e) => onChange({ ...element, position: { ...element.position, y: parseFloat(e.target.value) || 0 } })}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              step="0.5"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">{t('creator:properties.width')}</label>
            <input
              type="number"
              value={Math.round(element.size.width * 10) / 10}
              onChange={(e) => {
                const newWidth = Math.max(5, parseFloat(e.target.value) || 10);
                const isCircle = element.type === 'shape' && element.style.shapeType === 'circle';

                if (element.lockAspectRatio || isCircle) {
                  // Visuelles Seitenverhältnis beibehalten
                  const originalVisualRatio = isCircle ? 1 : (element.size.width * canvas.width) / (element.size.height * canvas.height);
                  // Neue visuelle Breite in Pixel
                  const newVisualWidthPx = (newWidth / 100) * canvas.width;
                  // Berechne entsprechende Höhe für gleiches visuelles Verhältnis
                  const newVisualHeightPx = newVisualWidthPx / originalVisualRatio;
                  const newHeight = (newVisualHeightPx / canvas.height) * 100;
                  onChange({ ...element, size: { width: newWidth, height: Math.max(5, newHeight) } });
                } else {
                  onChange({ ...element, size: { ...element.size, width: newWidth } });
                }
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              step="0.5"
              min="5"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500">{t('creator:properties.height')}</label>
            <input
              type="number"
              value={Math.round(element.size.height * 10) / 10}
              onChange={(e) => {
                const newHeight = Math.max(5, parseFloat(e.target.value) || 10);
                const isCircle = element.type === 'shape' && element.style.shapeType === 'circle';

                if (element.lockAspectRatio || isCircle) {
                  // Visuelles Seitenverhältnis beibehalten
                  const originalVisualRatio = isCircle ? 1 : (element.size.width * canvas.width) / (element.size.height * canvas.height);
                  // Neue visuelle Höhe in Pixel
                  const newVisualHeightPx = (newHeight / 100) * canvas.height;
                  // Berechne entsprechende Breite für gleiches visuelles Verhältnis
                  const newVisualWidthPx = newVisualHeightPx * originalVisualRatio;
                  const newWidth = (newVisualWidthPx / canvas.width) * 100;
                  onChange({ ...element, size: { width: Math.max(5, newWidth), height: newHeight } });
                } else {
                  onChange({ ...element, size: { ...element.size, height: newHeight } });
                }
              }}
              className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
              step="0.5"
              min="5"
            />
          </div>
        </div>
        {/* Seitenverhaeltnis sperren */}
        <div className="flex items-center justify-between mt-2">
          <span className="text-xs text-gray-500">{t('creator:properties.lockAspectRatio')}</span>
          <button
            onClick={() => onChange({ ...element, lockAspectRatio: !element.lockAspectRatio })}
            className={`p-1.5 rounded transition-colors ${
              element.lockAspectRatio
                ? 'bg-blue-100 text-blue-600'
                : 'hover:bg-gray-100 text-gray-400'
            }`}
            title={element.lockAspectRatio ? t('creator:properties.unlockAspectRatio') : t('creator:properties.lockAspectRatio')}
          >
            {element.lockAspectRatio ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Text-spezifische Eigenschaften */}
      {element.type === 'text' && (
        <>
          {/* Text-Inhalt mit Emoji-Picker */}
          <TextInputWithEmoji
            value={element.content || ''}
            onChange={updateContent}
          />

          {/* Schriftart */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.fontFamily')}</label>
            <select
              value={element.style.fontFamily || 'Avenir Next'}
              onChange={(e) => updateStyle({ fontFamily: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              style={{ fontFamily: element.style.fontFamily || 'Avenir Next' }}
            >
              <optgroup label={t('creator:properties.fontCategories.handwriting')}>
                {FONTS.filter(f => f.category === 'script').map((font) => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                ))}
              </optgroup>
              <optgroup label={t('creator:properties.fontCategories.modernSans')}>
                {FONTS.filter(f => f.category === 'sans').map((font) => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                ))}
              </optgroup>
              <optgroup label={t('creator:properties.fontCategories.serif')}>
                {FONTS.filter(f => f.category === 'serif').map((font) => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                ))}
              </optgroup>
              <optgroup label={t('creator:properties.fontCategories.display')}>
                {FONTS.filter(f => f.category === 'display').map((font) => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                ))}
              </optgroup>
              <optgroup label={t('creator:properties.fontCategories.monospace')}>
                {FONTS.filter(f => f.category === 'mono').map((font) => (
                  <option key={font.value} value={font.value} style={{ fontFamily: font.value }}>{font.label}</option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Schriftgroesse & Gewicht */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.fontSize')}</label>
              <input
                type="number"
                value={element.style.fontSize || 24}
                onChange={(e) => updateStyle({ fontSize: parseInt(e.target.value) || 24 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="8"
                max="200"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.lineHeight')}</label>
              <input
                type="number"
                value={element.style.lineHeight || 1.2}
                onChange={(e) => updateStyle({ lineHeight: parseFloat(e.target.value) || 1.2 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0.5"
                max="3"
                step="0.1"
              />
            </div>
          </div>

          {/* Text-Styling Buttons */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => updateStyle({ fontWeight: element.style.fontWeight === 'bold' ? 'normal' : 'bold' })}
              className={`p-2 rounded ${element.style.fontWeight === 'bold' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title={t('creator:properties.bold')}
            >
              <span className="font-bold">B</span>
            </button>
            <button
              onClick={() => updateStyle({ fontStyle: element.style.fontStyle === 'italic' ? 'normal' : 'italic' })}
              className={`p-2 rounded ${element.style.fontStyle === 'italic' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title={t('creator:properties.italic')}
            >
              <span className="italic">I</span>
            </button>
            <button
              onClick={() => updateStyle({ textDecoration: element.style.textDecoration === 'underline' ? 'none' : 'underline' })}
              className={`p-2 rounded ${element.style.textDecoration === 'underline' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title={t('creator:properties.underline')}
            >
              <span className="underline">U</span>
            </button>
            <button
              onClick={() => updateStyle({ textDecoration: element.style.textDecoration === 'line-through' ? 'none' : 'line-through' })}
              className={`p-2 rounded ${element.style.textDecoration === 'line-through' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title={t('creator:properties.strikethrough')}
            >
              <span className="line-through">S</span>
            </button>
            <div className="w-px h-6 bg-gray-200 mx-1" />
            <button
              onClick={() => updateStyle({ textAlign: 'left' })}
              className={`p-2 rounded ${element.style.textAlign === 'left' || !element.style.textAlign ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title={t('creator:properties.textAlignLeft')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8M4 18h12" />
              </svg>
            </button>
            <button
              onClick={() => updateStyle({ textAlign: 'center' })}
              className={`p-2 rounded ${element.style.textAlign === 'center' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title={t('creator:properties.textAlignCenter')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M8 12h8M6 18h12" />
              </svg>
            </button>
            <button
              onClick={() => updateStyle({ textAlign: 'right' })}
              className={`p-2 rounded ${element.style.textAlign === 'right' ? 'bg-blue-100 text-blue-600' : 'hover:bg-gray-100'}`}
              title={t('creator:properties.textAlignRight')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M12 12h8M8 18h12" />
              </svg>
            </button>
          </div>

          {/* Vertikale Ausrichtung */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.verticalAlign')}</label>
            <select
              value={element.style.verticalAlign || 'top'}
              onChange={(e) => updateStyle({ verticalAlign: e.target.value as 'top' | 'middle' | 'bottom' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="top">{t('creator:properties.verticalTop')}</option>
              <option value="middle">{t('creator:properties.verticalMiddle')}</option>
              <option value="bottom">{t('creator:properties.verticalBottom')}</option>
            </select>
          </div>

          {/* Textfarbe */}
          <ColorPicker
            color={element.style.color || '#000000'}
            onChange={(color) => updateStyle({ color })}
            label={t('creator:properties.textColor')}
          />

          {/* Hintergrundfarbe */}
          <ColorPicker
            color={element.style.backgroundColor || 'transparent'}
            onChange={(color) => updateStyle({ backgroundColor: color })}
            label={t('creator:properties.backgroundColor')}
            showAlpha
            allowTransparent
          />

          {/* Eckenradius fuer Textbox */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.cornerRadius')}</label>
            <input
              type="range"
              value={element.style.borderRadius || 0}
              onChange={(e) => updateStyle({ borderRadius: parseInt(e.target.value) })}
              className="w-full"
              min="0"
              max="50"
            />
            <div className="text-sm text-gray-500 text-right">{element.style.borderRadius || 0}px</div>
          </div>

          {/* Textbox-Rahmen */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.borderWidth')}</label>
              <input
                type="number"
                value={element.style.borderWidth || 0}
                onChange={(e) => updateStyle({ borderWidth: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0"
                max="20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.borderStyle')}</label>
              <select
                value={element.style.borderStyle || 'solid'}
                onChange={(e) => updateStyle({ borderStyle: e.target.value as 'solid' | 'dashed' | 'dotted' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="solid">{t('creator:properties.borderSolid')}</option>
                <option value="dashed">{t('creator:properties.borderDashed')}</option>
                <option value="dotted">{t('creator:properties.borderDotted')}</option>
              </select>
            </div>
          </div>

          {/* Rahmenfarbe (nur anzeigen wenn Rahmenbreite > 0) */}
          {(element.style.borderWidth || 0) > 0 && (
            <ColorPicker
              color={element.style.borderColor || '#000000'}
              onChange={(color) => updateStyle({ borderColor: color })}
              label={t('creator:properties.borderColor')}
            />
          )}

          {/* Innenabstand (Padding) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.padding')}</label>
            <input
              type="range"
              value={element.style.padding || 0}
              onChange={(e) => updateStyle({ padding: parseInt(e.target.value) })}
              className="w-full"
              min="0"
              max="40"
            />
            <div className="text-sm text-gray-500 text-right">{element.style.padding || 0}px</div>
          </div>
        </>
      )}

      {/* Shape-spezifische Eigenschaften */}
      {element.type === 'shape' && (
        <>
          {/* Form-Typ */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.shapeType')}</label>
            <select
              value={element.style.shapeType || 'rectangle'}
              onChange={(e) => {
                const newShapeType = e.target.value as ElementStyle['shapeType'];
                // Bei Kreis automatisch Seitenverhaeltnis sperren UND visuell quadratisch machen
                if (newShapeType === 'circle') {
                  // Berechne die groessere Seite in Pixeln und nutze diese als Referenz
                  const currentWidthPx = (element.size.width / 100) * canvas.width;
                  const currentHeightPx = (element.size.height / 100) * canvas.height;
                  // Nutze den Durchschnitt der beiden Seiten fuer einen perfekten Kreis
                  const targetSizePx = (currentWidthPx + currentHeightPx) / 2;
                  // Konvertiere zurueck in Prozent fuer Breite und Hoehe
                  const newWidthPercent = (targetSizePx / canvas.width) * 100;
                  const newHeightPercent = (targetSizePx / canvas.height) * 100;

                  onChange({
                    ...element,
                    lockAspectRatio: true,
                    size: {
                      width: newWidthPercent,
                      height: newHeightPercent,
                    },
                    style: { ...element.style, shapeType: newShapeType },
                  });
                } else {
                  updateStyle({ shapeType: newShapeType });
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              {SHAPE_TYPE_KEYS.map((type) => (
                <option key={type.value} value={type.value}>{t(`creator:properties.shapes.${type.key}`)}</option>
              ))}
            </select>
          </div>

          {/* Fuellung - Toggle zwischen Farbe und Gradient */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-gray-700">{t('creator:properties.fill')}</label>
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => updateStyle({ backgroundGradient: undefined })}
                  className={`px-2 py-1 text-xs font-medium transition-colors ${
                    !element.style.backgroundGradient
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t('creator:color')}
                </button>
                <button
                  type="button"
                  onClick={() => updateStyle({
                    backgroundGradient: element.style.backgroundGradient || `linear-gradient(180deg, ${element.style.backgroundColor || '#3B82F6'} 0%, #764ba2 100%)`,
                    backgroundColor: undefined
                  })}
                  className={`px-2 py-1 text-xs font-medium transition-colors ${
                    element.style.backgroundGradient
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {t('creator:gradient')}
                </button>
              </div>
            </div>

            {element.style.backgroundGradient ? (
              <GradientPicker
                value={element.style.backgroundGradient}
                onChange={(gradient) => updateStyle({ backgroundGradient: gradient, backgroundColor: undefined })}
              />
            ) : (
              <ColorPicker
                color={element.style.backgroundColor || '#3B82F6'}
                onChange={(color) => updateStyle({ backgroundColor: color, backgroundGradient: undefined })}
                showAlpha
                allowTransparent
              />
            )}
          </div>

          {/* Nur Rahmen */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={element.style.strokeOnly || false}
              onChange={(e) => updateStyle({ strokeOnly: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">{t('creator:properties.strokeOnly')}</span>
          </label>

          {/* Rahmen */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.borderWidth')}</label>
              <input
                type="number"
                value={element.style.borderWidth || 0}
                onChange={(e) => updateStyle({ borderWidth: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0"
                max="20"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.borderStyle')}</label>
              <select
                value={element.style.borderStyle || 'solid'}
                onChange={(e) => updateStyle({ borderStyle: e.target.value as 'solid' | 'dashed' | 'dotted' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              >
                <option value="solid">{t('creator:properties.borderSolid')}</option>
                <option value="dashed">{t('creator:properties.borderDashed')}</option>
                <option value="dotted">{t('creator:properties.borderDotted')}</option>
              </select>
            </div>
          </div>

          {/* Rahmenfarbe */}
          <ColorPicker
            color={element.style.borderColor || '#000000'}
            onChange={(color) => updateStyle({ borderColor: color })}
            label={t('creator:properties.borderColor')}
          />

          {/* Border Radius (nur fuer Rechteck) */}
          {element.style.shapeType === 'rectangle' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.cornerRadius')}</label>
              <input
                type="range"
                value={element.style.borderRadius || 0}
                onChange={(e) => updateStyle({ borderRadius: parseInt(e.target.value) })}
                className="w-full"
                min="0"
                max="100"
              />
              <div className="text-sm text-gray-500 text-right">{element.style.borderRadius || 0}px</div>
            </div>
          )}

          {/* Bild-Fuellung fuer Shapes */}
          <div className="border-t border-gray-200 pt-3 mt-3">
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('creator:properties.imageFill')}</label>

            {/* Aktuelles Bild anzeigen */}
            {element.content && (
              <div className="mb-2 relative">
                <img
                  src={element.content}
                  alt={t('creator:properties.fill')}
                  className="w-full h-24 object-cover rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => updateContent('')}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  title={t('creator:properties.removeImage')}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* MediaPicker Button */}
            {onOpenMediaPicker && (
              <button
                onClick={onOpenMediaPicker}
                className="w-full px-3 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {element.content ? t('creator:properties.changeImage') : t('creator:properties.imageAsFill')}
              </button>
            )}

            {/* objectFit fuer Shape-Bilder */}
            {element.content && (
              <>
                <div className="mt-2">
                  <label className="block text-xs text-gray-500 mb-1">{t('creator:properties.imageAdjustment')}</label>
                  <select
                    value={element.style.objectFit || 'cover'}
                    onChange={(e) => updateStyle({ objectFit: e.target.value as 'cover' | 'contain' | 'fill' })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded"
                  >
                    <option value="cover">{t('creator:properties.objectFit.cover')}</option>
                    <option value="contain">{t('creator:properties.objectFit.contain')}</option>
                    <option value="fill">{t('creator:properties.objectFit.fill')}</option>
                  </select>
                </div>

                {/* Bild-Position innerhalb der Form */}
                <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-xs font-medium text-gray-600">{t('creator:properties.imagePositionInShape')}</label>
                    {(element.style.imageOffsetX || element.style.imageOffsetY || (element.style.imageScale && element.style.imageScale !== 1)) && (
                      <button
                        onClick={() => updateStyle({ imageOffsetX: 0, imageOffsetY: 0, imageScale: 1 })}
                        className="text-xs text-blue-500 hover:text-blue-700"
                      >
                        {t('common:reset')}
                      </button>
                    )}
                  </div>

                  {/* Bild-Zoom */}
                  <div className="mb-2">
                    <label className="text-xs text-gray-400">{t('creator:properties.zoom')}</label>
                    <input
                      type="range"
                      value={((element.style.imageScale || 1) - 1) * 50}
                      onChange={(e) => updateStyle({ imageScale: 1 + parseInt(e.target.value) / 50 })}
                      className="w-full"
                      min="0"
                      max="100"
                    />
                    <div className="text-xs text-gray-500 text-right">{Math.round((element.style.imageScale || 1) * 100)}%</div>
                  </div>

                  {/* X/Y Offset */}
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-xs text-gray-400">{t('creator:properties.horizontal')}</label>
                      <input
                        type="range"
                        value={element.style.imageOffsetX || 0}
                        onChange={(e) => updateStyle({ imageOffsetX: parseInt(e.target.value) })}
                        className="w-full"
                        min="-50"
                        max="50"
                      />
                      <div className="text-xs text-gray-500 text-right">{element.style.imageOffsetX || 0}</div>
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">{t('creator:properties.vertical')}</label>
                      <input
                        type="range"
                        value={element.style.imageOffsetY || 0}
                        onChange={(e) => updateStyle({ imageOffsetY: parseInt(e.target.value) })}
                        className="w-full"
                        min="-50"
                        max="50"
                      />
                      <div className="text-xs text-gray-500 text-right">{element.style.imageOffsetY || 0}</div>
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {t('creator:properties.moveImageInShape')}
                  </p>
                </div>
              </>
            )}

            <p className="text-xs text-gray-400 mt-2">
              {t('creator:properties.imageInShapeInfo')}
            </p>
          </div>
        </>
      )}

      {/* Image-spezifische Eigenschaften */}
      {element.type === 'image' && (
        <>
          {/* Medien-Auswahl */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('creator:properties.selectImage')}</label>

            {/* Aktuelles Bild anzeigen */}
            {element.content && (
              <div className="mb-2 relative">
                <img
                  src={element.content}
                  alt={t('creator:properties.selectedImage')}
                  className="w-full h-32 object-cover rounded-lg border border-gray-200"
                />
                <button
                  onClick={() => updateContent('')}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                  title={t('creator:properties.removeImage')}
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            )}

            {/* MediaPicker Button */}
            {onOpenMediaPicker && (
              <button
                onClick={onOpenMediaPicker}
                className="w-full px-4 py-2 text-sm font-medium text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {t('creator:properties.fromMediaLibrary')}
              </button>
            )}

            {/* Manuelle URL-Eingabe */}
            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">{t('creator:properties.orEnterUrl')}</label>
              <input
                type="text"
                value={element.content || ''}
                onChange={(e) => updateContent(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                placeholder={t('creator:properties.urlPlaceholder')}
              />
            </div>
          </div>

          {/* Object Fit */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.imageAdjustment')}</label>
            <select
              value={element.style.objectFit || 'cover'}
              onChange={(e) => updateStyle({ objectFit: e.target.value as 'cover' | 'contain' | 'fill' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="cover">{t('creator:properties.objectFit.cover')}</option>
              <option value="contain">{t('creator:properties.objectFit.contain')}</option>
              <option value="fill">{t('creator:properties.objectFit.fill')}</option>
            </select>
          </div>

          {/* Border Radius */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.cornerRadius')}</label>
            <input
              type="range"
              value={element.style.borderRadius || 0}
              onChange={(e) => updateStyle({ borderRadius: parseInt(e.target.value) })}
              className="w-full"
              min="0"
              max="200"
            />
            <div className="text-sm text-gray-500 text-right">{element.style.borderRadius || 0}px</div>
          </div>
        </>
      )}

      {/* Audio-spezifische Eigenschaften */}
      {element.type === 'audio' && (
        <>
          {/* Audio-Auswahl */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">{t('creator:properties.audioFile')}</label>

            {/* Aktuelles Audio anzeigen */}
            {element.content && (
              <div className="mb-2 p-3 bg-gradient-to-r from-purple-100 to-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center gap-2">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                  </svg>
                  <span className="text-sm text-purple-800 truncate flex-1">
                    {element.content.split('/').pop() || t('creator:audio')}
                  </span>
                  <button
                    onClick={() => updateContent('')}
                    className="p-1 text-purple-500 hover:text-purple-700"
                    title={t('creator:properties.removeAudio')}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                {/* Audio-Vorschau */}
                <audio controls className="w-full mt-2 h-8" src={element.content}>
                  {t('creator:properties.audioNotSupported')}
                </audio>
              </div>
            )}

            {/* MediaPicker Button */}
            {onOpenMediaPicker && (
              <button
                onClick={onOpenMediaPicker}
                className="w-full px-4 py-2 text-sm font-medium text-purple-600 border border-purple-300 rounded-lg hover:bg-purple-50 flex items-center justify-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                </svg>
                {t('creator:properties.fromMediaLibrary')}
              </button>
            )}

            {/* Manuelle URL-Eingabe */}
            <div className="mt-3">
              <label className="block text-xs text-gray-500 mb-1">{t('creator:properties.orEnterUrl')}</label>
              <input
                type="text"
                value={element.content || ''}
                onChange={(e) => updateContent(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                placeholder="https://... (.mp3, .wav, .ogg)"
              />
            </div>
          </div>

          {/* Lautstaerke */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.volume')}</label>
            <input
              type="range"
              value={element.style.volume ?? 100}
              onChange={(e) => updateStyle({ volume: parseInt(e.target.value) })}
              className="w-full"
              min="0"
              max="100"
            />
            <div className="text-sm text-gray-500 text-right">{element.style.volume ?? 100}%</div>
          </div>

          {/* Fade-In */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.fadeIn')}</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                value={element.style.fadeInDuration || 0}
                onChange={(e) => updateStyle({ fadeInDuration: parseFloat(e.target.value) })}
                className="flex-1"
                min="0"
                max="10"
                step="0.5"
              />
              <span className="text-sm text-gray-500 w-12 text-right">{element.style.fadeInDuration || 0}s</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('creator:properties.soundFadesIn')}</p>
          </div>

          {/* Fade-Out */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.fadeOut')}</label>
            <div className="flex items-center gap-2">
              <input
                type="range"
                value={element.style.fadeOutDuration || 0}
                onChange={(e) => updateStyle({ fadeOutDuration: parseFloat(e.target.value) })}
                className="flex-1"
                min="0"
                max="10"
                step="0.5"
              />
              <span className="text-sm text-gray-500 w-12 text-right">{element.style.fadeOutDuration || 0}s</span>
            </div>
            <p className="text-xs text-gray-400 mt-1">{t('creator:properties.soundFadesOut')}</p>
          </div>

          {/* Wiederholen */}
          <label className="flex items-center gap-2 mt-2">
            <input
              type="checkbox"
              checked={element.style.audioLoop || false}
              onChange={(e) => updateStyle({ audioLoop: e.target.checked })}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700">{t('creator:properties.loop')}</span>
          </label>

          {/* Startzeit / Endzeit */}
          <div className="grid grid-cols-2 gap-2 mt-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.startTime')}</label>
              <input
                type="number"
                value={element.style.audioStartTime || 0}
                onChange={(e) => updateStyle({ audioStartTime: parseFloat(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                min="0"
                step="0.5"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('creator:properties.endTime')}</label>
              <input
                type="number"
                value={element.style.audioEndTime || ''}
                onChange={(e) => updateStyle({ audioEndTime: e.target.value ? parseFloat(e.target.value) : undefined })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder={t('creator:properties.endPlaceholder')}
                min="0"
                step="0.5"
              />
            </div>
          </div>
          <p className="text-xs text-gray-400">{t('creator:properties.audioClipInfo')}</p>

          {/* Info-Box fuer Reels/Stories */}
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-blue-700">
                <p className="font-medium mb-1">{t('creator:properties.soundForReels')}</p>
                <p>{t('creator:properties.audioHint')}</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Allgemeine Eigenschaften */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">{t('creator:properties.general')}</h4>

        {/* Deckkraft */}
        <div>
          <label className="block text-sm text-gray-600 mb-1">{t('creator:properties.opacity')}</label>
          <input
            type="range"
            value={(element.style.opacity ?? 1) * 100}
            onChange={(e) => updateStyle({ opacity: parseInt(e.target.value) / 100 })}
            className="w-full"
            min="0"
            max="100"
          />
          <div className="text-sm text-gray-500 text-right">{Math.round((element.style.opacity ?? 1) * 100)}%</div>
        </div>

        {/* Rotation */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-600">{t('creator:properties.rotation')}</label>
            {element.style.rotation !== 0 && element.style.rotation && (
              <button
                onClick={() => updateStyle({ rotation: 0 })}
                className="text-xs text-blue-500 hover:text-blue-700"
              >
                {t('common:reset')}
              </button>
            )}
          </div>
          <input
            type="range"
            value={element.style.rotation || 0}
            onChange={(e) => updateStyle({ rotation: parseInt(e.target.value) })}
            className="w-full"
            min="-180"
            max="180"
          />
          <div className="text-sm text-gray-500 text-right">{element.style.rotation || 0}°</div>
        </div>

        {/* 3D-Rotation (X/Y-Achse) */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-600">{t('creator:properties.tilt3d')}</label>
            {(element.style.rotateX || element.style.rotateY || element.style.perspective) && (
              <button
                onClick={() => updateStyle({ rotateX: 0, rotateY: 0, perspective: undefined })}
                className="text-xs text-blue-500 hover:text-blue-700"
              >
                {t('common:reset')}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-400">{t('creator:properties.xAxis')}</label>
              <input
                type="range"
                value={element.style.rotateX || 0}
                onChange={(e) => updateStyle({ rotateX: parseInt(e.target.value) })}
                className="w-full"
                min="-90"
                max="90"
              />
              <div className="text-xs text-gray-500 text-right">{element.style.rotateX || 0}°</div>
            </div>
            <div>
              <label className="text-xs text-gray-400">{t('creator:properties.yAxis')}</label>
              <input
                type="range"
                value={element.style.rotateY || 0}
                onChange={(e) => updateStyle({ rotateY: parseInt(e.target.value) })}
                className="w-full"
                min="-90"
                max="90"
              />
              <div className="text-xs text-gray-500 text-right">{element.style.rotateY || 0}°</div>
            </div>
          </div>
          {/* Perspektive nur anzeigen wenn 3D-Rotation aktiv */}
          {(element.style.rotateX || element.style.rotateY) && (
            <div className="mt-2">
              <label className="text-xs text-gray-400">{t('creator:properties.perspective')}</label>
              <input
                type="range"
                value={element.style.perspective || 1000}
                onChange={(e) => updateStyle({ perspective: parseInt(e.target.value) })}
                className="w-full"
                min="100"
                max="2000"
              />
              <div className="text-xs text-gray-500 text-right">{element.style.perspective || 1000}px</div>
            </div>
          )}
        </div>

        {/* Scheren (Skew) */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-600">{t('creator:properties.shearDistort')}</label>
            {(element.style.skewX || element.style.skewY) && (
              <button
                onClick={() => updateStyle({ skewX: 0, skewY: 0 })}
                className="text-xs text-blue-500 hover:text-blue-700"
              >
                {t('common:reset')}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs text-gray-400">{t('creator:properties.horizontal')}</label>
              <input
                type="range"
                value={element.style.skewX || 0}
                onChange={(e) => updateStyle({ skewX: parseInt(e.target.value) })}
                className="w-full"
                min="-45"
                max="45"
              />
              <div className="text-xs text-gray-500 text-right">{element.style.skewX || 0}°</div>
            </div>
            <div>
              <label className="text-xs text-gray-400">{t('creator:properties.vertical')}</label>
              <input
                type="range"
                value={element.style.skewY || 0}
                onChange={(e) => updateStyle({ skewY: parseInt(e.target.value) })}
                className="w-full"
                min="-45"
                max="45"
              />
              <div className="text-xs text-gray-500 text-right">{element.style.skewY || 0}°</div>
            </div>
          </div>
        </div>

        {/* Elevation / 3D-Effekt */}
        <div className="mt-3">
          <div className="flex items-center justify-between mb-1">
            <label className="text-sm text-gray-600">{t('creator:properties.depth3d')}</label>
            {element.style.elevation && element.style.elevation > 0 && (
              <button
                onClick={() => updateStyle({ elevation: 0 })}
                className="text-xs text-blue-500 hover:text-blue-700"
              >
                {t('common:reset')}
              </button>
            )}
          </div>
          <input
            type="range"
            value={element.style.elevation || 0}
            onChange={(e) => updateStyle({ elevation: parseInt(e.target.value) })}
            className="w-full"
            min="0"
            max="24"
          />
          <div className="text-sm text-gray-500 text-right">{element.style.elevation || 0}</div>
        </div>

        {/* Alle Transformationen zuruecksetzen */}
        {(element.style.rotation || element.style.rotateX || element.style.rotateY ||
          element.style.skewX || element.style.skewY || element.style.elevation) && (
          <div className="mt-4 pt-3 border-t border-gray-200">
            <button
              onClick={() => updateStyle({
                rotation: 0,
                rotateX: 0,
                rotateY: 0,
                skewX: 0,
                skewY: 0,
                perspective: undefined,
                elevation: 0,
              })}
              className="w-full px-3 py-2 text-sm text-orange-600 border border-orange-300 rounded-lg hover:bg-orange-50"
            >
              {t('creator:properties.resetAllTransforms')}
            </button>
          </div>
        )}

        {/* Liniendicke - nur fuer Shapes */}
        {element.type === 'shape' && (
          <div className="mt-3">
            <label className="block text-sm text-gray-600 mb-1">{t('creator:properties.strokeWidth')}</label>
            <input
              type="range"
              value={element.style.strokeWidth || 0}
              onChange={(e) => updateStyle({ strokeWidth: parseInt(e.target.value) })}
              className="w-full"
              min="0"
              max="20"
            />
            <div className="text-sm text-gray-500 text-right">{element.style.strokeWidth || 0}px</div>
          </div>
        )}
      </div>
    </div>
  );
}
